import { ReactElement, useState } from 'react'
import { ActionCreatorWithPayload } from '@reduxjs/toolkit'
import Avatar from '@mui/material/Avatar'
import Chip from '@mui/material/Chip'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { CancelIcon } from 'src/v3/core/icons'
import { useAppDispatch } from 'src/store/hooks'

type EntityData = {
  id: string
  name: string
  imageName: string
}

interface Props<T extends EntityData> {
  name: string
  entities: T[]
  entityID: string
  activeEntityChanged: ActionCreatorWithPayload<string>
}

function SelectEntity<T extends EntityData>({
  name,
  entities,
  entityID,
  activeEntityChanged,
}: Props<T>): ReactElement {
  let dispatch = useAppDispatch()

  let [entityChip, setEntityChip] = useState<null | HTMLElement>(null)
  let menuOpen = Boolean(entityChip)

  let handleSelectActiveEntity = (id: string) => () => {
    dispatch(activeEntityChanged(id))
  }

  let handleSelectorOpen = (event: React.MouseEvent<HTMLElement>) => {
    setEntityChip(event.currentTarget)
  }

  let handleSelectorClose = () => {
    setEntityChip(null)
  }

  let entity = entities.find((s) => s.id === entityID)

  return (
    <span>
      <Chip
        color="primary"
        variant={entity?.name ? 'filled' : 'outlined'}
        label={entity?.name ?? `Select a ${name}`}
        avatar={
          <Avatar
            src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${entity?.imageName}`}
          >
            {(entity?.name || name)
              .split(' ')
              .map((x) => x[0])
              .join('')}
          </Avatar>
        }
        onClick={handleSelectorOpen}
      />
      <Menu
        anchorEl={entityChip}
        id="account-menu"
        open={menuOpen}
        onClose={handleSelectorOpen}
        onClick={handleSelectorClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'scroll',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleSelectActiveEntity('unselected')}>
          <Avatar>
            <CancelIcon />
          </Avatar>
          Remove Selection
        </MenuItem>
        {entities.map((s) => (
          <MenuItem key={s.id} onClick={handleSelectActiveEntity(s.id ?? '')}>
            <Avatar
              src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${s?.imageName}`}
            >
              {(s?.name || name)
                .split(' ')
                .map((x) => x[0])
                .join('')}
            </Avatar>{' '}
            {s.name}
          </MenuItem>
        ))}
      </Menu>
    </span>
  )
}

export default SelectEntity
